import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import GroupRemoveOutlinedIcon from "@mui/icons-material/GroupRemoveOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { brand } from "../../../../theme/themePrimitives";

type ConfirmApplyModalProps = {
  open: boolean;
  addedCount: number;
  removedCount: number;
  onCancel: () => void;
  onConfirm: () => void;
  loading?: boolean;
};

export const ConfirmApplyModal: React.FC<ConfirmApplyModalProps> = ({
  open,
  addedCount,
  removedCount,
  onCancel,
  onConfirm,
  loading = false,
}) => (
  <Modal open={open} onClose={onCancel}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 544,
        bgcolor: "white",
        borderRadius: "12px",
        boxShadow: 24,
        outline: "none",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p="16px 16px 0 16px"
      >
        <Typography variant="h6" fontWeight={600}>
          Apply these changes?
        </Typography>
        <IconButton onClick={onCancel} sx={{ border: "none" }}>
          <CloseIcon sx={{ color: "#98A2B3" }} />
        </IconButton>
      </Box>

      <Box
        display="flex"
        alignItems="flex-start"
        gap="6px"
        p="0 16px 16px 16px"
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 16, color: "#DC6803", mt: 0.2, flexShrink: 0 }}
        />
        <Typography fontSize={14} sx={{ color: "#DC6803" }}>
          Saving this rule will automatically update assignments for matched
          users. Review the impact before confirming.
        </Typography>
      </Box>

      <Divider />

      <Stack spacing={1.5} p={2}>
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{
            bgcolor: "#F9FAFB",
            borderRadius: "12px",
            p: "16px",
            boxShadow: "0px 1px 2px 0px #1018280F",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#ECFDF3",
              border: "1px solid #DBFAE6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <GroupAddOutlinedIcon sx={{ fontSize: 20, color: "#079455" }} />
          </Box>
          <Box>
            <Typography fontWeight={600} fontSize={20} color="#085D3A">
              {addedCount}
            </Typography>
            <Typography fontSize={12} color="#475467">
              {addedCount === 1 ? "user" : "users"} will be{" "}
              <Box component="span" fontWeight={600}>
                assigned
              </Box>{" "}
              this course
            </Typography>
          </Box>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{
            bgcolor: "#F9FAFB",
            borderRadius: "12px",
            p: "16px",
            boxShadow: "0px 1px 2px 0px #1018280F",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#FFFAEB",
              border: "1px solid #FEF0C7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <GroupRemoveOutlinedIcon sx={{ fontSize: 20, color: "#DC6803" }} />
          </Box>
          <Box>
            <Typography fontWeight={600} fontSize={20} color="#DC6803">
              {removedCount}
            </Typography>
            <Typography fontSize={12} color="#475467">
              {removedCount === 1 ? "user" : "users"} will have this assignment{" "}
              <Box component="span" fontWeight={600}>
                removed
              </Box>
            </Typography>
          </Box>
        </Box>
      </Stack>

      <Box display="flex" justifyContent="flex-end" gap={1.5} p={2}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          sx={{
            bgcolor: `${brand[600]} !important`,
            color: "white",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            p: "10px 16px",
            "&:hover": { bgcolor: `${brand[700]} !important` },
            "&.Mui-disabled": { color: "white", bgcolor: `${brand[600]} !important` },
          }}
        >
          {loading ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} thickness={5} sx={{ color: "white" }} />
              <span>Confirm and apply</span>
            </Stack>
          ) : (
            "Confirm and apply"
          )}
        </Button>
      </Box>
    </Box>
  </Modal>
);
